import { useState } from 'react';

import type { BlindFavorabilityResult } from '@/services/blind-favorability-service';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface WindFavorabilitySectionProps {
  /** Favorability results for each qualifying blind/stand. Pass empty array to hide. */
  rows: BlindFavorabilityResult[];
  /**
   * Current wind direction string (e.g. "NW 12 mph" label for display).
   * When null, shows "Wind data unavailable" instead of per-row content.
   */
  windLabel: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function WindFavorabilitySection({ rows, windLabel }: WindFavorabilitySectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Section hidden when no blinds have ideal wind directions configured
  if (rows.length === 0) return null;

  return (
    <div data-testid="wind-favorability-section">
      {/* Accordion header */}
      <button
        type="button"
        className="w-full mt-1 flex items-center justify-between text-left"
        aria-expanded={isExpanded}
        data-testid="wind-favorability-accordion-header"
        onClick={() => setIsExpanded((v) => !v)}
      >
        <span className="text-[10px] font-bold uppercase tracking-widest text-[#9a9590]">
          Wind Favorability
        </span>
        <svg
          className={`shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#9a9590"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {/* Accordion body */}
      {isExpanded && (
        <div className="mt-1 flex flex-col gap-0.5" data-testid="wind-favorability-rows">
          {windLabel === null ? (
            <p className="text-[10px] text-[#9a9590]" data-testid="wind-data-unavailable">
              Wind data unavailable
            </p>
          ) : (
            rows.map((result) => (
              <p
                key={result.locationId}
                className="text-[10px] text-[#9a9590]"
                data-testid={`wind-favorability-${result.locationId}`}
              >
                {result.favorable ? (
                  <span className="text-[#4a8c5c]" aria-label="favorable">✓</span>
                ) : (
                  <span className="text-[#c8651a]" aria-label="unfavorable">✗</span>
                )}{' '}
                {result.favorable ? (
                  <span className="text-[#4a8c5c]">Favorable</span>
                ) : (
                  <span className="text-[#c8651a]">Unfavorable</span>
                )}{' '}
                {result.name}{' '}
                <span className="text-[#9a9590]">{windLabel}</span>
              </p>
            ))
          )}
        </div>
      )}
    </div>
  );
}
